import i18next from "i18next";
import i18n from "@/i18n";

// Debug mode flag - set to false to disable verbose logging
const DEBUG_MODE = false;

/**
 * This middleware helps debug and fix i18next issues by tracking
 * missing translations and logging helpful information.
 */
export function setupI18nMiddleware() {
  // First, remove any existing event listeners to prevent duplicates
  i18n.off("missingKey");
  i18n.off("failedLoading");
  i18n.off("languageChanged");

  // Log the current language immediately
  if (DEBUG_MODE)
    console.log(`[i18nMiddleware] Current language: ${i18n.language}`);

  // Check if all required namespaces are loaded
  const availableResources = i18n.services.resourceStore.data;
  if (DEBUG_MODE)
    console.log(
      `[i18nMiddleware] Available languages: ${Object.keys(availableResources).join(", ")}`,
    );

  // Add event listener for language changes
  i18n.on("languageChanged", (lang) => {
    if (DEBUG_MODE)
      console.log(`[i18nMiddleware] Language changed to: ${lang}`);

    // Force a refresh to update all components
    i18n.emit("loaded");
  });

  // Keep track of missing keys for better debugging
  const missingKeyTracker = new Set<string>();

  // Add debug middleware
  i18n.on("missingKey", (lng, ns, key) => {
    const fullKey = `${ns}:${key}`;
    if (!missingKeyTracker.has(fullKey)) {
      missingKeyTracker.add(fullKey);
      // Only log in debug mode or to the tracker component
      if (DEBUG_MODE)
        console.warn(`[i18n] Missing translation: ${lng}/${ns}:${key}`);

      // Special handler for navigation namespace issues
      if (key.startsWith("nav.") && ns !== "navigation" && DEBUG_MODE) {
        // Navigation keys should be in the navigation namespace
        console.warn(
          `[i18n] Navigation key "${key}" found in "${ns}" namespace but should be in "navigation" namespace`,
        );

        // Check if it exists in the navigation namespace
        if (i18n.exists(key, { ns: "navigation" })) {
          console.log(
            `[i18n] Navigation key exists in "navigation" namespace, use "navigation:${key}" instead`,
          );
        }
      }

      // Check if the key exists in other namespaces
      const allNamespaces = i18n.options?.ns as string[] | undefined;
      if (allNamespaces && DEBUG_MODE) {
        for (const otherNs of allNamespaces) {
          if (otherNs !== ns) {
            // Try both with and without namespace prefix in the key
            if (i18n.exists(key, { ns: otherNs })) {
              console.info(
                `[i18n] Found key "${key}" in namespace "${otherNs}" instead of "${ns}"`,
              );
            }
          }
        }
      }
    }
  });

  // Add a listener for failed resource loading
  i18n.on("failedLoading", (lng, ns, msg) => {
    if (!DEBUG_MODE) return; // Skip if not in debug mode

    console.error(`[i18n] Failed to load resource ${lng}/${ns}: ${msg}`);

    // Try to diagnose the issue
    const backend = i18n.options.backend as { loadPath?: string };
    const expectedPath = backend?.loadPath
      ?.replace("{{lng}}", lng)
      ?.replace("{{ns}}", ns);

    console.error(`[i18n] Expected resource path: ${expectedPath}`);

    // Report diagnostic information
    console.error(`[i18n] Current config:`, {
      backend: i18n.options.backend,
      ns: i18n.options.ns,
      defaultNS: i18n.options.defaultNS,
      languages: i18n.languages,
      currentLanguage: i18n.language,
    });

    // Try to fetch the resource manually to see if it exists
    if (typeof window !== "undefined" && expectedPath) {
      fetch(expectedPath)
        .then((response) => {
          if (!response.ok) {
            console.error(
              `[i18n] Manual fetch failed with status ${response.status} for ${expectedPath}`,
            );
          } else {
            console.log(
              `[i18n] Manual fetch succeeded for ${expectedPath} but i18next still failed to load it`,
            );
          }
        })
        .catch((error) => {
          console.error(
            `[i18n] Manual fetch error for ${expectedPath}:`,
            error,
          );
        });
    }
  });

  // Expose debugging utilities
  type I18nDebugUtility = {
    forceLoadNamespace: (ns: string, lng: string) => Promise<void>;
    getResourceBundle: (
      ns: string,
      lng: string,
    ) => Record<string, unknown> | undefined;
    listMissingKeys: () => string[];
    listLoadedNamespaces: () => string[] | undefined;
    checkKey: (
      key: string,
      ns?: string,
    ) => { exists: boolean; foundIn: string[] };
    enableDebugMode: (enable: boolean) => void;
  };

  const debugUtility: I18nDebugUtility = {
    forceLoadNamespace: async (ns: string, lng: string) => {
      try {
        if (DEBUG_MODE)
          console.info(
            `[i18n] Attempting to force load namespace ${ns} for language ${lng}`,
          );
        await i18n.loadNamespaces(ns);
        const hasResources = i18n.hasResourceBundle(lng, ns);
        if (DEBUG_MODE)
          console.info(
            `[i18n] After forced load, resources for ${lng}/${ns}: ${hasResources ? "Loaded" : "Still not loaded"}`,
          );

        // If still not loaded, try to manually fetch and add the resources
        if (!hasResources) {
          if (DEBUG_MODE)
            console.warn(
              `[i18n] Namespace ${ns} not loaded, attempting direct fetch`,
            );
          try {
            // Try both src/locales and /locales paths
            const paths = [
              `/locales/${lng}/${ns}.json`,
              `/src/locales/${lng}/${ns}.json`,
            ];

            for (const path of paths) {
              if (DEBUG_MODE)
                console.log(`[i18n] Trying to fetch from ${path}`);
              try {
                const response = await fetch(path);
                if (response.ok) {
                  const data = await response.json();
                  i18n.addResourceBundle(lng, ns, data, true, true);
                  if (DEBUG_MODE)
                    console.info(
                      `[i18n] Manually added resource bundle for ${lng}/${ns} from ${path}`,
                    );
                  break; // Success, no need to try other paths
                } else if (DEBUG_MODE) {
                  console.warn(
                    `[i18n] Failed to fetch ${path}: ${response.status}`,
                  );
                }
              } catch (e) {
                if (DEBUG_MODE)
                  console.warn(`[i18n] Error fetching from ${path}:`, e);
              }
            }
          } catch (e) {
            if (DEBUG_MODE)
              console.error(`[i18n] Error force loading namespace ${ns}:`, e);
          }
        }
      } catch (e) {
        if (DEBUG_MODE)
          console.error(`[i18n] Error force loading namespace ${ns}:`, e);
      }
    },
    getResourceBundle: (ns: string, lng: string) =>
      i18n.getResourceBundle(lng, ns) as Record<string, unknown> | undefined,
    listMissingKeys: () => Array.from(missingKeyTracker),
    listLoadedNamespaces: () => i18n.options?.ns as string[] | undefined,
    checkKey: (key: string, ns?: string) => {
      const result = { exists: false, foundIn: [] as string[] };
      const allNamespaces = i18n.options?.ns as string[] | undefined;
      if (allNamespaces) {
        allNamespaces.forEach((namespace) => {
          if (i18n.exists(key, { ns: namespace })) {
            result.exists = true;
            result.foundIn.push(namespace);
          }
        });
      }
      return result;
    },
    enableDebugMode: (enable: boolean) => {
      (window as unknown as { __i18n_debug_mode?: boolean }).__i18n_debug_mode =
        enable;
      console.log(`[i18n] Debug mode ${enable ? "enabled" : "disabled"}`);
    },
  };

  // Expose the debug utility
  if (typeof window !== "undefined" && process.env.NODE_ENV === "development") {
    (window as unknown as { __i18n_debug: I18nDebugUtility }).__i18n_debug =
      debugUtility;
  }

  return i18n;
}
